import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import apiTypes from 'src/api/optimalprint-sdk.d';
import { MESSAGE_TYPE } from 'src/store/app/types';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { Store } from 'src/store/index';
import clipArtToAddon from 'src/util/addon/clipArtToAddon';
import collectAllUsedAddons from 'src/util/addon/collectAllUsedAddons';
import getStructure from 'src/store/design/selector/getStructure';
import getPrice from 'src/store/price/selector/getPrice';
import getCategoryId from 'src/store/design/selector/getCategoryId';
import getPriceFormat from 'src/store/price/selector/getPriceFormat';
import getCurrencyId from 'src/store/price/selector/getCurrencyId';
import formatPrice from 'src/util/price/formatPrice';
import productPriceToPrice from 'src/util/price/productPriceToPrice';
import fetchClipArtsPriceOperation from './fetchClipArtsPriceOperation';
import calculateUsedClipArtsPriceOperation from './calculateUsedClipArtsPriceOperation';
import { addUsedClipArtsAction } from '../slice';

// Fallback product ID for Design API cliparts
const DEFAULT_CLIPART_PRODUCT_ID = 3914;

// Helper to check if an ID is a UUID (Design API clipart)
const isUuid = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

// Fetch legacy cliparts from the old API (numeric IDs stored in design_clip_art table)
const fetchLegacyClipArtsInUse = async (designId: number) => {
  try {
    const clipArtsInUse = (await opApiRequest(
      endpoints.CUSTOMER_DESIGN_V3_GET_CLIP_ARTS,
      {
        designId,
      },
      'data.clipArts',
      'POST',
    )) as apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignGetClipArtsV3Response.IClipArt[];
    return clipArtsInUse || [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not fetch legacy cliparts in use, exception: ', e);
    return [];
  }
};

// Fetch Design API cliparts (UUIDs) with fresh URLs
const fetchDesignApiClipArts = async (
  uuidIds: string[],
): Promise<apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignGetClipArtsV3Response.IClipArt[]> => {
  if (uuidIds.length === 0) {
    return [];
  }

  try {
    const response = (await opApiRequest(
      endpoints.PUBLIC_ASSET_V1_LIST,
      { ids: uuidIds },
      'data',
      'GET',
    )) as { assets: any[]; productId?: number } | null;

    if (!response || !response.assets) {
      return [];
    }

    const productId = response.productId ?? DEFAULT_CLIPART_PRODUCT_ID;

    return response.assets.map((asset) => {
      const previewFile = asset.files?.find((f: any) => f.type === 'preview_default');
      return {
        clipArtId: asset.id,
        title: asset.name,
        description: asset.description || null,
        imageUrl: previewFile?.url || '',
        productId,
        tags: asset.tags || null,
        clipArtCategoryIds: asset.category?.categoryId ? [asset.category.categoryId] : null,
        allowColorChange: true,
        quantity: 1,
      };
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not fetch Design API cliparts, exception: ', e);
    return [];
  }
};

const fetchAndSendClipArtInUseOperation = (designId: number) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  const store = getState();
  const categoryId = getCategoryId(store);
  const priceFormat = getPriceFormat(store) || '';
  const currencyId = getCurrencyId(store) || '';
  const designStructure = getStructure(store);

  // Fetch legacy cliparts from old API
  const legacyClipArts = await fetchLegacyClipArtsInUse(designId);

  // Extract UUID cliparts from design structure and fetch fresh data
  let designApiClipArts: apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignGetClipArtsV3Response.IClipArt[] = [];
  if (designStructure) {
    const usedAddons = collectAllUsedAddons(designStructure);
    const uuidIds = usedAddons
      .map((addon) => addon.clipArtId)
      .filter((id) => isUuid(id));

    if (uuidIds.length > 0) {
      designApiClipArts = await fetchDesignApiClipArts(uuidIds);
      // Update quantities from design structure
      designApiClipArts.forEach((clipArt) => {
        const foundAddon = usedAddons.find((addon) => addon.clipArtId === String(clipArt.clipArtId));
        if (foundAddon) {
          clipArt.quantity = foundAddon.quantity;
        }
      });
    }
  }

  // Combine legacy and Design API cliparts
  const allClipArtsInUse = [...legacyClipArts, ...designApiClipArts];

  const getAddonPrice = (productId: number) => {
    const priceObj = getPrice(getState(), categoryId || 1, productId, 1);
    if (!priceObj) {
      return '';
    }
    return formatPrice(productPriceToPrice(priceObj).price, priceFormat, currencyId);
  };

  await dispatch(fetchClipArtsPriceOperation({
    clipArts: allClipArtsInUse,
    subCategories: [],
  }));

  if (allClipArtsInUse.length > 0) {
    const addonsToSend = allClipArtsInUse.map((clipArt) => clipArtToAddon(clipArt, getAddonPrice));
    sendPostMessage({
      type: MESSAGE_TYPE['addons.setAddonsInUse'],
      data: addonsToSend,
    });
    dispatch(addUsedClipArtsAction(allClipArtsInUse));
    if (designStructure) {
      await dispatch(calculateUsedClipArtsPriceOperation(designStructure));
    }
  }
};

export default fetchAndSendClipArtInUseOperation;
